import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  OpenQuestion,
  QuestionStatus,
  QuestionPriority,
  QuestionAnalytics,
  QuestionAnswer,
} from '@/models/team-question.model';
import {
  auditCreate,
  auditUpdate,
  auditDelete,
} from '@/utils/audit-integration';
import { AuditEntityType, AuditLogAction } from '@/models/audit-log.model';
import { AuditLogService } from './audit-log.service';

const QUESTIONS_COLLECTION = 'teamOpenQuestions';

export class TeamQuestionService {
  /**
   * Create a new question (typically from AI extraction)
   */
  static async createQuestion(
    questionData: Partial<OpenQuestion>,
    userId: string = 'ai-system',
    userName: string = 'AI System'
  ): Promise<string> {
    const now = Timestamp.now();

    const question: Partial<OpenQuestion> = {
      ...questionData,
      status: questionData.status || QuestionStatus.draft,
      priority: questionData.priority || QuestionPriority.medium,
      containsPHI: questionData.containsPHI || false,
      phiDataTypes: questionData.phiDataTypes || [],
      tags: questionData.tags || [],
      followUpRequired: questionData.followUpRequired || false,
      relatedDecisionIds: questionData.relatedDecisionIds || [],
      relatedTaskIds: questionData.relatedTaskIds || [],
      relatedRiskIds: questionData.relatedRiskIds || [],
      answers: questionData.answers || [],
      createdBy: userId,
      createdByName: userName,
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, QUESTIONS_COLLECTION), question);

    // Audit log
    await auditCreate(
      AuditEntityType.teamQuestion,
      docRef.id,
      questionData.question || 'Untitled Question'
    );

    return docRef.id;
  }

  /**
   * Get question by ID
   */
  static async getQuestionById(id: string): Promise<OpenQuestion | null> {
    const docRef = doc(db, QUESTIONS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return this.docToQuestion(docSnap.id, docSnap.data());
  }

  /**
   * Get all questions for a meeting
   */
  static async getQuestionsByMeeting(meetingId: string): Promise<OpenQuestion[]> {
    const q = query(
      collection(db, QUESTIONS_COLLECTION),
      where('meetingId', '==', meetingId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToQuestion(doc.id, doc.data()));
  }

  /**
   * Get all questions
   */
  static async getAllQuestions(): Promise<OpenQuestion[]> {
    const q = query(
      collection(db, QUESTIONS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToQuestion(doc.id, doc.data()));
  }

  /**
   * Get questions by status
   */
  static async getQuestionsByStatus(status: QuestionStatus): Promise<OpenQuestion[]> {
    const q = query(
      collection(db, QUESTIONS_COLLECTION),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToQuestion(doc.id, doc.data()));
  }

  /**
   * Get questions by priority
   */
  static async getQuestionsByPriority(priority: QuestionPriority): Promise<OpenQuestion[]> {
    const q = query(
      collection(db, QUESTIONS_COLLECTION),
      where('priority', '==', priority),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToQuestion(doc.id, doc.data()));
  }

  /**
   * Get questions assigned to a user
   */
  static async getQuestionsByAssignee(assignedToId: string): Promise<OpenQuestion[]> {
    const q = query(
      collection(db, QUESTIONS_COLLECTION),
      where('assignedToId', '==', assignedToId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToQuestion(doc.id, doc.data()));
  }

  /**
   * Get open questions (not answered, deferred, or invalidated)
   */
  static async getOpenQuestions(): Promise<OpenQuestion[]> {
    const allQuestions = await this.getAllQuestions();
    return allQuestions.filter(
      q =>
        q.status !== QuestionStatus.answered &&
        q.status !== QuestionStatus.deferred &&
        q.status !== QuestionStatus.invalidated
    );
  }

  /**
   * Search questions
   */
  static async searchQuestions(searchTerm: string): Promise<OpenQuestion[]> {
    const allQuestions = await this.getAllQuestions();
    const lowerSearch = searchTerm.toLowerCase();

    return allQuestions.filter(question =>
      question.question.toLowerCase().includes(lowerSearch) ||
      question.context?.toLowerCase().includes(lowerSearch) ||
      question.category?.toLowerCase().includes(lowerSearch)
    );
  }

  /**
   * Update question
   */
  static async updateQuestion(
    id: string,
    updates: Partial<OpenQuestion>,
    userId?: string
  ): Promise<void> {
    const docRef = doc(db, QUESTIONS_COLLECTION, id);

    const updateData: any = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    if (userId) {
      updateData.updatedBy = userId;
    }

    await updateDoc(docRef, updateData);

    // Audit log
    await auditUpdate(
      AuditEntityType.teamQuestion,
      id,
      updates.question || 'Question'
    );
  }

  /**
   * Delete question
   */
  static async deleteQuestion(id: string): Promise<void> {
    const question = await this.getQuestionById(id);
    if (!question) {
      throw new Error('Question not found');
    }

    const docRef = doc(db, QUESTIONS_COLLECTION, id);
    await deleteDoc(docRef);

    // Audit log
    await auditDelete(
      AuditEntityType.teamQuestion,
      id,
      question.question
    );
  }

  /**
   * Assign question to a user
   */
  static async assignQuestion(
    id: string,
    assignedToId: string,
    assignedTo: string,
    assignedBy?: string
  ): Promise<void> {
    const question = await this.getQuestionById(id);
    if (!question) {
      throw new Error('Question not found');
    }

    await this.updateQuestion(id, {
      assignedToId,
      assignedTo,
      assignedAt: new Date(),
      status: QuestionStatus.investigating,
    }, assignedBy);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.questionAssigned,
      severity: 'info',
      description: `Question assigned to ${assignedTo}: ${question.question}`,
      userId: assignedBy,
      entityType: AuditEntityType.teamQuestion,
      entityId: id,
      entityName: question.question,
      success: true,
      details: {
        assignedToId,
        assignedTo,
      },
    });
  }

  /**
   * Answer a question
   */
  static async answerQuestion(
    id: string,
    answer: string,
    answeredById: string,
    answeredBy: string,
    answerSource?: string
  ): Promise<void> {
    const question = await this.getQuestionById(id);
    if (!question) {
      throw new Error('Question not found');
    }

    await this.updateQuestion(id, {
      answer,
      answeredById,
      answeredBy,
      answeredAt: new Date(),
      answerSource,
      status: QuestionStatus.answered,
    }, answeredById);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.questionAnswered,
      severity: 'info',
      description: `Question answered: ${question.question}`,
      userId: answeredById,
      entityType: AuditEntityType.teamQuestion,
      entityId: id,
      entityName: question.question,
      success: true,
      details: {
        answeredBy,
        answerSource,
      },
    });
  }

  /**
   * Add an answer to the answers array (for multiple answers)
   */
  static async addAnswer(
    id: string,
    answerText: string,
    respondentId: string,
    respondentName: string
  ): Promise<void> {
    const question = await this.getQuestionById(id);
    if (!question) {
      throw new Error('Question not found');
    }

    const newAnswer: QuestionAnswer = {
      id: `answer-${Date.now()}`,
      text: answerText,
      respondentId,
      respondentName,
      timestamp: new Date(),
      isAccepted: false,
    };

    const answers = [...(question.answers || []), newAnswer];

    await this.updateQuestion(id, { answers }, respondentId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.questionAnswered,
      severity: 'info',
      description: `Answer added to question: ${question.question}`,
      userId: respondentId,
      entityType: AuditEntityType.teamQuestion,
      entityId: id,
      entityName: question.question,
      success: true,
      details: {
        respondentName,
      },
    });
  }

  /**
   * Accept an answer from the answers array
   */
  static async acceptAnswer(
    id: string,
    answerId: string,
    userId?: string
  ): Promise<void> {
    const question = await this.getQuestionById(id);
    if (!question) {
      throw new Error('Question not found');
    }

    const answers = question.answers?.map(a =>
      a.id === answerId ? { ...a, isAccepted: true } : { ...a, isAccepted: false }
    );

    const acceptedAnswer = answers?.find(a => a.id === answerId);

    await this.updateQuestion(id, {
      answers,
      acceptedAnswerId: answerId,
      answer: acceptedAnswer?.text,
      answeredById: acceptedAnswer?.respondentId,
      answeredBy: acceptedAnswer?.respondentName,
      answeredAt: new Date(),
      status: QuestionStatus.answered,
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.questionAnswered,
      severity: 'info',
      description: `Answer accepted for question: ${question.question}`,
      userId,
      entityType: AuditEntityType.teamQuestion,
      entityId: id,
      entityName: question.question,
      success: true,
      details: {
        answerId,
        respondent: acceptedAnswer?.respondentName,
      },
    });
  }

  /**
   * Defer a question
   */
  static async deferQuestion(
    id: string,
    deferredUntil: Date,
    reason: string,
    userId?: string
  ): Promise<void> {
    const question = await this.getQuestionById(id);
    if (!question) {
      throw new Error('Question not found');
    }

    await this.updateQuestion(id, {
      status: QuestionStatus.deferred,
      deferredUntil,
      deferralReason: reason,
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.questionDeferred,
      severity: 'info',
      description: `Question deferred: ${question.question}`,
      userId,
      entityType: AuditEntityType.teamQuestion,
      entityId: id,
      entityName: question.question,
      success: true,
      details: {
        deferredUntil: deferredUntil.toISOString(),
        reason,
      },
    });
  }

  /**
   * Invalidate a question (no longer relevant)
   */
  static async invalidateQuestion(
    id: string,
    reason: string,
    userId?: string,
    userName?: string
  ): Promise<void> {
    const question = await this.getQuestionById(id);
    if (!question) {
      throw new Error('Question not found');
    }

    await this.updateQuestion(id, {
      status: QuestionStatus.invalidated,
      invalidatedReason: reason,
      invalidatedAt: new Date(),
      invalidatedBy: userName,
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.questionInvalidated,
      severity: 'info',
      description: `Question invalidated: ${question.question}`,
      userId,
      entityType: AuditEntityType.teamQuestion,
      entityId: id,
      entityName: question.question,
      success: true,
      details: {
        reason,
      },
    });
  }

  /**
   * Reopen a question
   */
  static async reopenQuestion(id: string, userId?: string): Promise<void> {
    const question = await this.getQuestionById(id);
    if (!question) {
      throw new Error('Question not found');
    }

    await this.updateQuestion(id, {
      status: QuestionStatus.open,
      answer: undefined,
      answeredById: undefined,
      answeredBy: undefined,
      answeredAt: undefined,
    }, userId);

    // Audit log
    await AuditLogService.writeLog({
      action: AuditLogAction.questionReopened,
      severity: 'info',
      description: `Question reopened: ${question.question}`,
      userId,
      entityType: AuditEntityType.teamQuestion,
      entityId: id,
      entityName: question.question,
      success: true,
    });
  }

  /**
   * Approve a question
   */
  static async approveQuestion(
    id: string,
    approverId: string,
    approverName: string,
    comments?: string
  ): Promise<void> {
    const question = await this.getQuestionById(id);
    if (!question) {
      throw new Error('Question not found');
    }

    // Update approval workflow
    const workflow = question.approvalWorkflow || [];
    const currentStep = workflow.find(step => step.status === 'pending');

    if (currentStep) {
      currentStep.status = 'approved';
      currentStep.approverId = approverId;
      currentStep.approverName = approverName;
      currentStep.actionDate = new Date();
      currentStep.comments = comments;
    }

    // Check if all steps are approved
    const allApproved = workflow.every(step => step.status === 'approved');

    await this.updateQuestion(id, {
      approvalWorkflow: workflow,
      status: allApproved ? QuestionStatus.open : question.status,
      approvedBy: allApproved ? approverId : undefined,
      approvedByName: allApproved ? approverName : undefined,
      approvedAt: allApproved ? new Date() : undefined,
    }, approverId);

    // Audit log
    await AuditLogService.writeLog({
      action: allApproved ? AuditLogAction.questionApproved : AuditLogAction.approvalStepCompleted,
      severity: 'info',
      description: `Question ${allApproved ? 'approved' : 'approval step completed'}: ${question.question}`,
      userId: approverId,
      entityType: AuditEntityType.teamQuestion,
      entityId: id,
      entityName: question.question,
      success: true,
      details: {
        approverName,
        allApproved,
        comments,
      },
    });
  }

  /**
   * Get question analytics
   */
  static async getQuestionAnalytics(
    dateRange?: { start: Date; end: Date }
  ): Promise<QuestionAnalytics> {
    let questions: OpenQuestion[];

    if (dateRange) {
      const q = query(
        collection(db, QUESTIONS_COLLECTION),
        where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
        where('createdAt', '<=', Timestamp.fromDate(dateRange.end))
      );
      const snapshot = await getDocs(q);
      questions = snapshot.docs.map(doc => this.docToQuestion(doc.id, doc.data()));
    } else {
      questions = await this.getAllQuestions();
    }

    const byStatus: Record<QuestionStatus, number> = {
      [QuestionStatus.draft]: 0,
      [QuestionStatus.open]: 0,
      [QuestionStatus.investigating]: 0,
      [QuestionStatus.answered]: 0,
      [QuestionStatus.deferred]: 0,
      [QuestionStatus.invalidated]: 0,
    };

    const byPriority: Record<QuestionPriority, number> = {
      [QuestionPriority.low]: 0,
      [QuestionPriority.medium]: 0,
      [QuestionPriority.high]: 0,
    };

    let totalTimeToAnswer = 0;
    let answeredCount = 0;
    let deferredCount = 0;
    let openCount = 0;

    questions.forEach(question => {
      byStatus[question.status]++;
      byPriority[question.priority]++;

      if (question.status === QuestionStatus.answered && question.createdAt && question.answeredAt) {
        const timeDiff = question.answeredAt.getTime() - question.createdAt.getTime();
        totalTimeToAnswer += timeDiff / (1000 * 60 * 60 * 24); // Convert to days
        answeredCount++;
      }

      if (question.status === QuestionStatus.deferred) {
        deferredCount++;
      }

      if (question.status === QuestionStatus.open || question.status === QuestionStatus.investigating) {
        openCount++;
      }
    });

    return {
      totalQuestions: questions.length,
      byStatus,
      byPriority,
      avgTimeToAnswer: answeredCount > 0 ? totalTimeToAnswer / answeredCount : 0,
      answeredCount,
      deferredCount,
      openCount,
    };
  }

  /**
   * Convert Firestore document to OpenQuestion model
   */
  private static docToQuestion(id: string, data: any): OpenQuestion {
    return {
      id,
      meetingId: data.meetingId || '',
      meetingTitle: data.meetingTitle || '',
      sourceTimestampStart: data.sourceTimestampStart,
      sourceTimestampEnd: data.sourceTimestampEnd,
      sourceTextSnippet: data.sourceTextSnippet,
      question: data.question || '',
      context: data.context,
      category: data.category || '',
      priority: data.priority || QuestionPriority.medium,
      assignedTo: data.assignedTo,
      assignedToId: data.assignedToId,
      assignedAt: data.assignedAt?.toDate(),
      answer: data.answer,
      answeredBy: data.answeredBy,
      answeredById: data.answeredById,
      answeredAt: data.answeredAt?.toDate(),
      answerSource: data.answerSource,
      answers: data.answers || [],
      acceptedAnswerId: data.acceptedAnswerId,
      status: data.status || QuestionStatus.draft,
      approvalWorkflow: data.approvalWorkflow || [],
      followUpRequired: data.followUpRequired || false,
      followUpTaskId: data.followUpTaskId,
      deferredUntil: data.deferredUntil?.toDate(),
      deferralReason: data.deferralReason,
      invalidatedReason: data.invalidatedReason,
      invalidatedAt: data.invalidatedAt?.toDate(),
      invalidatedBy: data.invalidatedBy,
      containsPHI: data.containsPHI || false,
      phiDataTypes: data.phiDataTypes || [],
      aiConfidence: data.aiConfidence || 0,
      aiExtractionNotes: data.aiExtractionNotes,
      tags: data.tags || [],
      relatedDecisionIds: data.relatedDecisionIds || [],
      relatedTaskIds: data.relatedTaskIds || [],
      relatedRiskIds: data.relatedRiskIds || [],
      customFields: data.customFields,
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      approvedBy: data.approvedBy,
      approvedByName: data.approvedByName,
      approvedAt: data.approvedAt?.toDate(),
    };
  }
}
